import { ROUTES } from "~/content/Routes";

export const SERVICE_CATEGORY_ORDER = [
  "Elektryczne",
  "Gazowe",
  "Kominiarskie i wentylacyjne",
  "Inne",
] as const;

export type ServiceCategoryLabel = (typeof SERVICE_CATEGORY_ORDER)[number];

export type ServiceIcon =
  | "plug-zap"
  | "gauge"
  | "cloud-lightning"
  | "sun"
  | "battery-charging"
  | "circuit-board"
  | "flame"
  | "activity"
  | "thermometer"
  | "clipboard-check"
  | "wind"
  | "air-vent"
  | "snowflake"
  | "thermometer-sun"
  | "bell-ring"
  | "shield-check"
  | "door-open"
  | "building-2";

export interface ServiceDefinition {
  slug: string;
  name: string;
  nameGenitive: string;
  shortName: string;
  navigationLabel?: string;
  description: string;
  longDescription?: string;
  icon: ServiceIcon;
  href: string;
  category: ServiceCategoryLabel;
  cityPages: boolean;
  legalBasis?: string;
  frequency?: string;
}

export const SERVICE_DEFINITIONS: ServiceDefinition[] = [
  {
    slug: "przeglad-instalacji-elektrycznej",
    name: "Przegląd Instalacji Elektrycznej",
    nameGenitive: "przeglądu instalacji elektrycznej",
    shortName: "Przegląd elektryczny",
    description:
      "Okresowa kontrola instalacji elektrycznej z oględzinami, pomiarami i protokołem dla domu, mieszkania lub budynku firmowego.",
    longDescription:
      "Przegląd instalacji elektrycznej obejmuje ocenę stanu przewodów, zabezpieczeń, rozdzielnicy, ochrony przeciwporażeniowej oraz podstawowe pomiary potrzebne do potwierdzenia bezpieczeństwa użytkowania instalacji. To usługa wykonywana obowiązkowo w ramach kontroli 5-letniej, ale też po remoncie, zakupie starszej nieruchomości, rozbudowie instalacji albo wtedy, gdy pojawiają się objawy usterek. Po kontroli właściciel otrzymuje protokół z wnioskami i zaleceniami dalszych działań.",
    icon: "plug-zap",
    href: ROUTES.ELECTRICAL_CHECK,
    category: "Elektryczne",
    cityPages: true,
    legalBasis: "art. 62 ust. 1 pkt 2 Prawa budowlanego",
    frequency: "co 5 lat",
  },
  {
    slug: "pomiary-elektryczne",
    name: "Pomiary Elektryczne",
    nameGenitive: "pomiarów elektrycznych",
    shortName: "Pomiary elektryczne",
    description:
      "Komplet pomiarów ochronnych i eksploatacyjnych instalacji elektrycznej z protokołem.",
    longDescription:
      "Pomiary elektryczne obejmują sprawdzenie kluczowych parametrów instalacji: rezystancji izolacji, ciągłości przewodów ochronnych, skuteczności ochrony przeciwporażeniowej, impedancji pętli zwarcia oraz działania zabezpieczeń różnicowoprądowych. To usługa potrzebna przy przeglądach okresowych, odbiorach po remoncie i diagnozie usterek. Protokół z pomiarów pozwala ocenić, czy instalacja nadaje się do dalszego bezpiecznego użytkowania.",
    icon: "gauge",
    href: ROUTES.ELECTRICAL_MEASUREMENTS,
    category: "Elektryczne",
    cityPages: false,
    legalBasis: "art. 62 ust. 1 pkt 2 Prawa budowlanego",
    frequency: "co 5 lat lub po zmianach",
  },
  {
    slug: "przeglad-instalacji-odgromowej",
    name: "Przegląd Instalacji Odgromowej",
    nameGenitive: "przeglądu instalacji odgromowej",
    shortName: "Instalacja odgromowa",
    description:
      "Pomiar rezystancji uziemienia i kontrola zwodów piorunochronnych. Norma PN-EN 62305.",
    longDescription:
      "Przegląd instalacji odgromowej obejmuje pomiar rezystancji uziemienia, kontrolę zwodów, odprowadzaczy i złączy probierczych oraz ocenę stanu technicznego całego systemu piorunochronnego. Protokół z pomiarami jest wymagany przez prawo budowlane i towarzystwa ubezpieczeniowe. Uszkodzony piorunochron może prowadzić do pożaru lub zniszczenia instalacji elektrycznej podczas wyładowania atmosferycznego.",
    icon: "cloud-lightning",
    href: ROUTES.LIGHTNING_CHECK,
    category: "Elektryczne",
    cityPages: true,
    legalBasis: "art. 62 ust. 1 pkt 2 Prawa budowlanego",
    frequency: "co 5 lat",
  },
  {
    slug: "przeglad-instalacji-fotowoltaicznej",
    name: "Przegląd Instalacji Fotowoltaicznej",
    nameGenitive: "przeglądu instalacji fotowoltaicznej",
    shortName: "Instalacja fotowoltaiczna",
    description:
      "Kontrola paneli, falownika, zabezpieczeń i pomiary instalacji PV z oceną współpracy z instalacją budynku.",
    longDescription:
      "Przegląd instalacji fotowoltaicznej obejmuje ocenę paneli PV, okablowania DC i AC, falownika, zabezpieczeń oraz podstawowych parametrów pracy systemu. To usługa dla właścicieli domów i firm, którzy chcą sprawdzić stan instalacji po montażu, po spadku uzysków, przed końcem gwarancji albo w ramach szerszej kontroli instalacji elektrycznej budynku.",
    icon: "sun",
    href: ROUTES.PV_INSTALLATION_INSPECTION,
    category: "Elektryczne",
    cityPages: false,
    legalBasis:
      "w ramach kontroli instalacji elektrycznej z art. 62 ust. 1 pkt 2 Prawa budowlanego",
    frequency:
      "zalecane co 1-2 lata, obowiązkowo w ramach kontroli 5-letniej instalacji elektrycznej",
  },
  {
    slug: "przeglad-magazynu-energii",
    name: "Przegląd Magazynu Energii",
    nameGenitive: "przeglądu magazynu energii",
    shortName: "Magazyn energii",
    description:
      "Kontrola połączeń, zabezpieczeń, BMS i warunków pracy magazynu energii w domu lub firmie.",
    longDescription:
      "Przegląd magazynu energii obejmuje ocenę stanu połączeń elektrycznych, zabezpieczeń DC i AC, komunikacji z falownikiem, pracy systemu BMS oraz warunków chłodzenia i wentylacji. To usługa dla właścicieli instalacji fotowoltaicznych i systemów backupowych, którzy chcą sprawdzić stan urządzenia po uruchomieniu, przed końcem gwarancji albo po pojawieniu się alarmów i spadku wydajności.",
    icon: "battery-charging",
    href: ROUTES.ENERGY_STORAGE_INSPECTION,
    category: "Elektryczne",
    cityPages: false,
    frequency: "zalecane co rok",
  },
  {
    slug: "rozdzielnia-elektryczna",
    name: "Rozdzielnia Elektryczna",
    nameGenitive: "rozdzielni elektrycznej",
    shortName: "Rozdzielnia",
    description:
      "Kontrola, modernizacja i wymiana rozdzielni elektrycznej wraz z oceną zabezpieczeń i stanu aparatury.",
    longDescription:
      "Usługa rozdzielnia elektryczna obejmuje ocenę stanu aparatury, połączeń, oznaczeń i zabezpieczeń, a także pomoc w podjęciu decyzji, czy wystarczy kontrola, czy potrzebna jest modernizacja lub pełna wymiana układu. To dobre rozwiązanie przy starych tablicach, rozbudowie instalacji o nowe odbiory mocy, częstym wyłączaniu zabezpieczeń albo przy przygotowaniu domu, mieszkania lub lokalu do bezpiecznej dalszej eksploatacji.",
    icon: "circuit-board",
    href: ROUTES.SWITCHBOARD_INSPECTION,
    category: "Elektryczne",
    cityPages: false,
    frequency: "według stanu instalacji i po zmianach",
  },
  {
    slug: "przeglad-instalacji-gazowej",
    name: "Przegląd Instalacji Gazowej",
    nameGenitive: "przeglądu instalacji gazowej",
    shortName: "Instalacja gazowa",
    description:
      "Obowiązkowy co roku. Prawo budowlane.",
    longDescription:
      "Przegląd instalacji gazowej obejmuje kontrolę szczelności wszystkich połączeń, sprawdzenie sprawności kurków odcinających oraz ocenę stanu przewodów i armatury. Uprawniony technik sprawdza też ciąg kominowy i wentylację w pomieszczeniach z urządzeniami gazowymi. Brak aktualnego przeglądu stanowi naruszenie Prawa budowlanego i może być podstawą do wstrzymania dostawy gazu przez dystrybutora.",
    icon: "flame",
    href: ROUTES.GAS_CHECK,
    category: "Gazowe",
    cityPages: true,
    legalBasis: "art. 62 ust. 1 pkt 1 lit. c Prawa budowlanego",
    frequency: "co rok",
  },
  {
    slug: "proba-szczelnosci-gazu",
    name: "Próba Szczelności Gazu",
    nameGenitive: "próby szczelności gazu",
    shortName: "Próba szczelności gazu",
    description:
      "Badanie szczelności nowej, przebudowanej lub ponownie uruchamianej instalacji gazowej z protokołem.",
    longDescription:
      "Próba szczelności gazu potwierdza, czy instalacja jest przygotowana do bezpiecznego uruchomienia po wykonaniu nowej instalacji, przebudowie, naprawie albo dłuższym postoju. Badanie pozwala wykryć nieszczelne połączenia, błędy montażowe i miejsca wymagające poprawy jeszcze przed oddaniem instalacji do użytkowania. Wynik próby i protokół są potrzebne przy odbiorach robót, przekazaniu lokalu inwestorowi oraz porządkowaniu dokumentacji technicznej.",
    icon: "activity",
    href: ROUTES.GAS_TIGHTNESS_TEST,
    category: "Gazowe",
    cityPages: true,
    legalBasis: "warunki techniczne wykonania i odbioru instalacji gazowych",
    frequency: "przed uruchomieniem lub po zmianach",
  },
  {
    slug: "przeglad-kotla-gazowego",
    name: "Przegląd Kotła Gazowego",
    nameGenitive: "przeglądu kotła gazowego",
    shortName: "Kocioł gazowy",
    description:
      "Roczny serwis kotła gazowego — czyszczenie palnika, pomiar spalin, kontrola szczelności.",
    longDescription:
      "Serwis kotła gazowego obejmuje czyszczenie palnika i wymiennika ciepła, pomiar spalin (CO, CO₂, sprawność), regulację parametrów pracy oraz kontrolę zabezpieczeń termostatycznych i manometrów. Zaniedbany kocioł zużywa o 10–15% więcej gazu i może produkować czad bez widocznych objawów. Coroczny przegląd jest wymagany przez producentów do utrzymania gwarancji na urządzenie.",
    icon: "thermometer",
    href: ROUTES.GAS_BOILER,
    category: "Gazowe",
    cityPages: true,
    frequency: "co rok",
  },
  {
    slug: "odbior-instalacji-gazowej",
    name: "Odbiór Instalacji Gazowej",
    nameGenitive: "odbioru instalacji gazowej",
    shortName: "Odbiór instalacji gazowej",
    description:
      "Kontrola wykonanej instalacji gazowej przed przekazaniem do użytkowania wraz z protokołem odbiorowym.",
    longDescription:
      "Odbiór instalacji gazowej jest potrzebny po wykonaniu nowej instalacji albo większej modernizacji, zanim układ zostanie przekazany do użytkowania. W czasie odbioru sprawdza się zgodność wykonania z dokumentacją, wyniki prób szczelności, stan armatury, dostęp do zaworów, warunki wentylacji i podłączenie urządzeń gazowych. Protokół odbiorowy porządkuje relację między inwestorem, wykonawcą i użytkownikiem oraz wskazuje, czy instalacja może przejść do kolejnego etapu uruchomienia.",
    icon: "clipboard-check",
    href: ROUTES.GAS_INSTALLATION_ACCEPTANCE,
    category: "Gazowe",
    cityPages: true,
    legalBasis: "dokumentacja projektowa i warunki odbioru instalacji gazowych",
    frequency: "po wykonaniu nowej instalacji lub modernizacji",
  },
  {
    slug: "przeglad-kominiarski",
    name: "Przegląd Kominiarski",
    nameGenitive: "przeglądu kominiarskiego",
    shortName: "Kominiarski",
    description:
      "Kontrola drożności i szczelności przewodów kominowych, dymowych i wentylacyjnych.",
    longDescription:
      "Przegląd kominiarski obejmuje ocenę drożności i szczelności przewodów spalinowych, dymowych i wentylacyjnych, a w przypadku kominów czynnych — czyszczenie sadzy i osadów smolistych. Mistrz kominiarski wystawia protokół wymagany przy zawarciu umowy ubezpieczenia nieruchomości i podczas przeglądu budowlanego. Niedrożny przewód kominowy to jedno z najczęstszych źródeł zatruć tlenkiem węgla w polskich mieszkaniach.",
    icon: "wind",
    href: ROUTES.CHIMNEY,
    category: "Kominiarskie i wentylacyjne",
    cityPages: true,
    legalBasis: "art. 62 ust. 1 pkt 1 lit. c Prawa budowlanego",
    frequency: "co rok",
  },
  {
    slug: "przeglad-wentylacji",
    name: "Przegląd Wentylacji Mechanicznej",
    nameGenitive: "przeglądu wentylacji",
    shortName: "Wentylacja",
    navigationLabel: "Wentylacja i Rekuperacja",
    description:
      "Serwis central wentylacyjnych, rekuperatorów i instalacji nawiewno-wywiewnych.",
    longDescription:
      "Przegląd wentylacji mechanicznej obejmuje pomiar przepływów powietrza na nawiewnikach i wywiewnikach, kontrolę central wentylacyjnych, filtrów i przepustnic oraz ocenę stanu rekuperatora lub chłodnicy. Technik reguluje też automatykę sterującą i sprawdza alarmy serwisowe systemu BMS. Zaniedbana wentylacja obniża jakość powietrza wewnętrznego, podnosi rachunki za energię i może być przyczyną problemów zdrowotnych mieszkańców lub pracowników.",
    icon: "air-vent",
    href: ROUTES.VENTILATION,
    category: "Kominiarskie i wentylacyjne",
    cityPages: true,
    frequency: "co rok",
  },
  {
    slug: "przeglad-klimatyzacji",
    name: "Przegląd Klimatyzacji",
    nameGenitive: "przeglądu klimatyzacji",
    shortName: "Klimatyzacja",
    description:
      "Serwis, czyszczenie i dezynfekcja klimatyzacji split, multisplit i urządzeń w domu, mieszkaniu lub biurze.",
    longDescription:
      "Przegląd klimatyzacji obejmuje ocenę stanu jednostki wewnętrznej i zewnętrznej, czyszczenie filtrów i wymienników, sprawdzenie odpływu skroplin, podstawową ocenę szczelności układu i parametrów pracy oraz dezynfekcję elementów narażonych na rozwój zanieczyszczeń biologicznych. To usługa potrzebna zarówno przed sezonem letnim, jak i po intensywnej eksploatacji, gdy urządzenie zaczyna słabiej chłodzić, wydaje niepokojące dźwięki albo powoduje nieprzyjemny zapach.",
    icon: "snowflake",
    href: ROUTES.AIR_CONDITIONING,
    category: "Kominiarskie i wentylacyjne",
    cityPages: false,
    frequency: "zalecane co rok, intensywnie używana nawet 2 razy w roku",
  },
  {
    slug: "przeglad-pompy-ciepla",
    name: "Przegląd Pompy Ciepła",
    nameGenitive: "przeglądu pompy ciepła",
    shortName: "Pompa ciepła",
    description:
      "Serwis i kontrola pompy ciepła z oceną parametrów pracy, czystości układu i stanu podstawowych podzespołów.",
    longDescription:
      "Przegląd pompy ciepła obejmuje ocenę parametrów pracy urządzenia, sprawdzenie obiegu grzewczego i chłodniczego w zakresie przewidzianym dla serwisu, kontrolę filtrów, połączeń, automatyki i podstawowych nastaw oraz weryfikację objawów mogących wskazywać na spadek sprawności lub zbliżającą się awarię. Regularny serwis pomaga utrzymać wydajność systemu, ogranicza ryzyko usterek w sezonie grzewczym i bywa wymagany przez producenta do zachowania warunków gwarancji.",
    icon: "thermometer-sun",
    href: ROUTES.HEAT_PUMP,
    category: "Kominiarskie i wentylacyjne",
    cityPages: false,
    frequency: "zalecane co rok",
  },
  {
    slug: "przeglad-systemow-wykrywania-pozaru",
    name: "Przegląd Systemów Wykrywania Pożaru",
    nameGenitive: "przeglądu systemów wykrywania pożaru",
    shortName: "Systemy wykrywania pożaru",
    navigationLabel: "Systemy Wykrywania Pożaru",
    description:
      "Kontrola autonomicznych czujników dymu oraz systemów SSP/SAP w ramach jednej usługi dla obiektu.",
    longDescription:
      "Usługa łączy przeglądy autonomicznych czujników dymu oraz bardziej rozbudowanych systemów sygnalizacji pożaru. Docelowo obejmie zarówno mniejsze obiekty, jak i budynki z centralą SSP/SAP, z naciskiem na sprawność wykrywania zagrożenia i aktualną dokumentację serwisową.",
    icon: "bell-ring",
    href: ROUTES.FIRE_DETECTION_INSPECTION,
    category: "Inne",
    cityPages: true,
    frequency: "zależnie od systemu, zwykle co kwartał lub co rok",
  },
  {
    slug: "przeglad-sprzetu-przeciwpozarowego",
    name: "Przegląd Sprzętu Przeciwpożarowego",
    nameGenitive: "przeglądu sprzętu przeciwpożarowego",
    shortName: "Sprzęt przeciwpożarowy",
    navigationLabel: "Sprzęt Przeciwpożarowy",
    description:
      "Kontrola gaśnic, hydrantów i podstawowego wyposażenia ppoż. wraz z dokumentacją dla obiektu.",
    longDescription:
      "Usługa grupuje okresowe przeglądy gaśnic i hydrantów w jedną stronę usługową. Docelowy landing będzie skupiał się na utrzymaniu sprzętu pierwszej reakcji, gotowości do kontroli i dokumentacji potrzebnej zarządcy albo właścicielowi obiektu.",
    icon: "shield-check",
    href: ROUTES.FIRE_EQUIPMENT_INSPECTION,
    category: "Inne",
    cityPages: true,
    frequency: "co rok",
  },
  {
    slug: "przeglad-zabezpieczen-ewakuacyjnych-i-przeciwpozarowych",
    name: "Przegląd Zabezpieczeń Ewakuacyjnych i Przeciwpożarowych",
    nameGenitive: "przeglądu zabezpieczeń ewakuacyjnych i przeciwpożarowych",
    shortName: "Zabezpieczenia ewakuacyjne",
    navigationLabel: "Zabezpieczenia Ewakuacyjne i Ppoż.",
    description:
      "Kontrola oświetlenia ewakuacyjnego, drzwi ppoż. i elementów wspierających bezpieczną ewakuację.",
    longDescription:
      "Ta strona scala temat oświetlenia ewakuacyjnego oraz drzwi przeciwpożarowych w jeden szerszy landing. Docelowo będzie opisywać elementy, które wspierają bezpieczne opuszczenie obiektu i utrzymanie podziału stref pożarowych podczas zagrożenia.",
    icon: "door-open",
    href: ROUTES.EVACUATION_FIRE_PROTECTION_INSPECTION,
    category: "Inne",
    cityPages: false,
    frequency: "co rok",
  },
  {
    slug: "przeglad-budowlany",
    name: "Przegląd Ogólnobudowlany",
    nameGenitive: "przeglądu ogólnobudowlanego",
    shortName: "Przegląd budowlany",
    description:
      "Roczna i pięcioletnia kontrola stanu technicznego budynku zgodnie z art. 62 Prawa budowlanego.",
    longDescription:
      "Przegląd ogólnobudowlany obejmuje ocenę stanu technicznego elementów konstrukcyjnych, dachu, elewacji, stolarki okiennej i drzwiowej oraz instalacji zewnętrznych. Coroczna kontrola jest obowiązkowa dla budynków o powierzchni zabudowy powyżej 2000 m², a pięcioletni przegląd dotyczy wszystkich obiektów. Brak protokołu podpisanego przez uprawnionego inspektora grozi grzywną i osobistą odpowiedzialnością właściciela lub zarządcy.",
    icon: "building-2",
    href: ROUTES.BUILDING_INSPECTION,
    category: "Inne",
    cityPages: true,
    legalBasis: "art. 62 ust. 1 pkt 1 i 2 Prawa budowlanego",
    frequency: "co rok + co 5 lat (pełny)",
  },
];
